import type { KnownGameConfig } from 'src/types/domain/GameConfig'
import { hasLightning, sideBetMultiplier } from './effectiveRtp'

/**
 * Maximum lightning-tier multiplier the game engine submits today —
 * legendary tier on the highest-base-multiplier marble.
 *   common   = 1.5x   | rare = 3x | epic = 4x | legendary = 5x
 * For display purposes (headline `Max multiplier` / `Max Payout`) we
 * quote the legendary headline since that's what the player could see
 * on a lucky round — but ONLY for games that actually have Lightning
 * configured. Coin Flip and crash have none, and multiplying their
 * ceiling by 5 advertised a payout that cannot occur.
 */
export const LIGHTNING_LEGENDARY_MULTIPLIER = 5

/**
 * Side-bet capability descriptor — mirrors the gates that
 * `<SideBetRows>` uses to decide whether forecast/tricast tiles
 * appear at all. When provided, `getEffectiveBetStats` factors the
 * highest-payout side-bet into the headline `maxMultiplier`.
 *
 *   forecast tiles render iff totalMarbles ≥ 2 AND maxPickableMarbles ≥ 2 AND resultsToSubmit ≥ 2
 *   tricast tiles render  iff totalMarbles ≥ 3 AND maxPickableMarbles ≥ 3 AND resultsToSubmit ≥ 3
 */
export interface SideBetGameInfo {
  totalMarbles: number
  maxPickableMarbles: number
  resultsToSubmit: number
}

export interface EffectiveBetStats {
  /**
   * Highest base multiplier the player could see WITHOUT any lightning
   * boost. Used for RTP calculations so the displayed RTP isn't
   * inflated by a rare event.
   */
  baseMaxMultiplier: number
  /**
   * Highest possible multiplier including the legendary lightning
   * boost. Used for headline `Max multiplier` / `Max Payout` displays.
   */
  maxMultiplier: number
  /**
   * Probability of the highest-multiplier outcome — used to compute
   * the headline RTP (base RTP, no lightning factor).
   */
  rtpProbability: number
  /**
   * Option id of the highest-multiplier outcome, so callers can resolve
   * its label (the marble colour) and look up the per-selection
   * effective RTP. Undefined for classic games, where the headline is
   * read off `bet` and no single option is identified.
   */
  rtpOptionId?: string
  /**
   * Count of bettable options in the main market — the `n` that side-bet
   * combinatorics must use. NOT `race_configuration.total_marbles`,
   * which reports 8 for Snake even though Snake has only 6 outcomes.
   */
  mainMarketOutcomes?: number
}

/** One bettable selection and the base RTP it pays. */
export interface BettableSelection {
  id: string
  baseRtp: number
}

/**
 * Every bettable selection in the game with its own base RTP
 * (multiplier x probability), across ALL markets.
 *
 * Callers pair this with `effectiveRtp` to describe a game whose headline
 * row isn't representative of what Lightning does. The CCTV counters are
 * the case that forced it: their highest-multiplier option is the 36x
 * Exact Boundary bet, whose ids are never in the boost pool, so quoting
 * only that row reported no Lightning at all for a game whose Race Winner
 * market is boosted up to 92.16%.
 *
 * Chip-placement only — classic games keep their headline on `bet` and
 * expose no per-option multipliers, so this returns [] for them.
 */
export function getBettableSelections(gameConfig: KnownGameConfig): BettableSelection[] {
  const out: BettableSelection[] = []
  for (const market of gameConfig.markets ?? []) {
    for (const option of market.options) {
      if (!option.bettable) continue
      const probability = market.probabilities?.[option.id]
      if (!probability || probability <= 0 || option.multiplier <= 0) continue
      out.push({ id: option.id, baseRtp: option.multiplier * probability * 100 })
    }
  }
  return out
}

/**
 * Returns the effective bet stats for either betting mode.
 *
 * - Classic games carry the headline values on `bet.maxMultiplier` and
 *   `bet.probabilities`. We use those directly.
 * - Chip-placement games leave `bet.maxMultiplier` / `bet.probabilities`
 *   empty by design (per-market state lives on `markets[]`). Without
 *   this util the info panel + RTP table show 0.00x. We scan the
 *   markets to find the highest-multiplier option across the game and
 *   read its probability from the matching market.
 *
 * The lightning legendary 5x factor is folded into `maxMultiplier` (the
 * headline) but NOT into `baseMaxMultiplier` (which the RTP column
 * uses), so a rare event doesn't artificially overstate the typical
 * RTP players see round-to-round.
 */
export function getEffectiveBetStats(
  gameConfig: KnownGameConfig,
  gameInfo?: SideBetGameInfo,
  gameKey?: string | null
): EffectiveBetStats {
  const isChipPlacement =
    gameConfig.bettingMode === 'chip-placement' &&
    gameConfig.markets !== undefined &&
    gameConfig.markets.length > 0

  if (isChipPlacement) {
    let baseMaxMultiplier = 0
    let rtpProbability = 0
    let rtpOptionId: string | undefined
    for (const market of gameConfig.markets ?? []) {
      for (const option of market.options) {
        if (option.multiplier > baseMaxMultiplier) {
          baseMaxMultiplier = option.multiplier
          rtpProbability =
            market.probabilities?.[option.id] ?? 1 / Math.max(1, market.options.length)
          rtpOptionId = option.id
        }
      }
    }

    // Side-bet combinatorics run over the MAIN market's bettable options.
    const mainMarket =
      gameConfig.markets?.find((m) => m.type === 'main') ?? gameConfig.markets?.[0]
    const mainMarketOutcomes =
      mainMarket?.options.filter((o) => o.bettable).length ?? mainMarket?.options.length ?? 0

    // Fold in side-bet ceilings when the game can actually host them.
    // The headline `Max multiplier` should reflect the highest payout
    // a player could realistically see — for forecast/tricast-eligible
    // games the tricast Exact Order on N marbles (n*(n-1)*(n-2)*margin)
    // dwarfs the main-market PickWinner. Without this the toolbar
    // showed e.g. 36.69x on 8-marble Plinko while the player could
    // see 285.6x on the side-bet rail right next to it.
    if (gameInfo) {
      const { totalMarbles, maxPickableMarbles, resultsToSubmit } = gameInfo
      const forecastEligible = totalMarbles >= 2 && maxPickableMarbles >= 2 && resultsToSubmit >= 2
      const tricastEligible = totalMarbles >= 3 && maxPickableMarbles >= 3 && resultsToSubmit >= 3
      // Combinatorics use the market's own outcome count, and the margin
      // comes from the game's own MarginConfig — the old flat 0.85 was
      // Marble Plinko's override applied to every game, which under-quoted
      // Rolling Dunes' tricast ceiling as 285.6x instead of 288.96x.
      const n = mainMarketOutcomes || totalMarbles
      if (forecastEligible) {
        // StraightForecast (Exact Order) — the higher of the two
        // forecast variants.
        const forecastMax = sideBetMultiplier(gameKey ?? null, 'StraightForecast', n)
        if (forecastMax > baseMaxMultiplier) {
          baseMaxMultiplier = forecastMax
        }
      }
      if (tricastEligible) {
        // Tricast (Exact Order) — the absolute ceiling across all
        // bet types.
        const tricastMax = sideBetMultiplier(gameKey ?? null, 'Tricast', n)
        if (tricastMax > baseMaxMultiplier) {
          baseMaxMultiplier = tricastMax
        }
      }
    }

    return {
      baseMaxMultiplier,
      // Only games with Lightning configured can reach the legendary
      // ceiling. Quoting base x 5 for a game without it promised a
      // payout that cannot occur.
      maxMultiplier: hasLightning(gameKey)
        ? baseMaxMultiplier * LIGHTNING_LEGENDARY_MULTIPLIER
        : baseMaxMultiplier,
      rtpProbability,
      rtpOptionId,
      mainMarketOutcomes
    }
  }

  // Classic: read directly off the bet config. `Math.min` of an empty
  // array is `Infinity` — guard against that so we don't ship NaN
  // through `calculateRTP`.
  const allProbs = Object.values(gameConfig.bet.probabilities ?? {})
  const minProb = allProbs.length > 0 ? Math.min(...allProbs) : 0
  return {
    baseMaxMultiplier: gameConfig.bet.maxMultiplier ?? 0,
    maxMultiplier: gameConfig.bet.maxMultiplier ?? 0,
    rtpProbability: minProb
  }
}
